import * as Actions from '../actions/expenseActions';
import Expense from '../../entities/expense';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ExpenseAction = { 
    type: string; 
    expense: Expense | null; 
    expenseId: string | Expense; 
    partial: boolean; 
    lastFetch: number; 
    expenses: Expense[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Expense> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Expense[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Expense> | null, firmId: string, userId: string, updates?: Expense[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'expense', 'expenses', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Expense>} data
 * @param {Expense[]} [updates]
 * @returns {ArrayMap<Expense>}
 */
function updateStorage(data: ArrayMap<Expense>, updates?: Expense[]): ArrayMap<Expense> {
	if (logging) console.debug('⮂ expenseReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Expense[]} [updates]
 * @returns {ArrayMap<Expense>}
 */
function updateStorageNoReturn(updates?: Expense[]): void {
	if (logging) console.debug('⮂ expenseReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Expense[]} [data]
 * @returns {ArrayMap<Expense>}
 */
function createState(data?: Expense[] | ArrayMap<Expense>): ArrayMap<Expense> {
	if (!data) return ArrayMapRegistry.clone<Expense>('expense', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Expense>('expense', data.map(e => e));
    else return ArrayMapRegistry.clone<Expense>('expense', data);
}

function expenses(state: ArrayMap<Expense>, action: ExpenseAction): ArrayMap<Expense> {
	if (!state) state = createState();

	let st: ArrayMap<Expense>;

	switch (action.type) {
		// action.expense - the new value of expense with id action.id
		// action.expenseId - id of the target, used if new value is null
		case Actions.EXPENSE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ expenseReducer: FETCH_ONE_SUCCESS', action);

			if (action.expense === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.expenseId)) {
				st = state.clone();
				st.set(action.expense);
				// state.clear();

				updateStorageNoReturn([action.expense]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.expenses
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.expenses
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.expenses
			 !partial, !stateEmpty, fetchEmpty	-> D return expenses in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge expenses in state newer than those in action.expenses
		 */
		case Actions.EXPENSE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.expenses.length === 0;
			const fetched = action.expenses.map(expense => new Expense({...expense, persisted: true}));

			if (logging) console.debug(`⮂ expenseReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} expenses with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ expenseReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ expenseReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Expense> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Expense[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(expense => {
					const current = st.get(expense.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && expense.status === 'D') {
							// Remove it from state
							toUpdate.push(expense);
							st.delete(expense);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (expense.addedOn > current.addedOn) {
								toUpdate.push(expense);
								st.set(expense);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (expense.status !== 'D') {
							toUpdate.push(expense);
							st.set(expense);
						}
						else if (logging) console.debug('⮂ expenseReducer: partialMerge: skipping deleted expense not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ expenseReducer: empty: expenses', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ expenseReducer: fullMerge: expenses', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localExpense => {
                    // If this expense has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localExpense.persisted) {
                        const fetchedExpense = st.get(localExpense.id);
                        if (!fetchedExpense || localExpense.addedOn > fetchedExpense.addedOn) st.set(localExpense);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.EXPENSE_FETCH_ONE_FAILURE:
			console.error('EXPENSE_FETCH_ONE_FAILURE', action.expenseId, action.err);

			// No update to storage required
			return state;

		case Actions.EXPENSE_FETCH_FAILURE:
			console.error('EXPENSE_FETCH_FAILURE', action.expenseId, action.err);

			// No update to storage required
			return state;

		case Actions.EXPENSE_RESTORE: {
		    let expense = new Expense({...action.expense});
            st = state.clone();

            const existing = st.get(expense.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (expense.addedOn >= existing.addedOn) {
                    st.set(expense);
                    return updateStorage(st, [expense]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (expense.status === 'D') return state;

                st.set(expense);
                return updateStorage(st, [expense]);
            }
        }
            
		case Actions.EXPENSE_SAVE: {
		    // console.debug('⮂ EXPENSE_SAVE reducer', action.expense?.id);
		    
			// clone the expense
			let expense = new Expense({...action.expense});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new expense in the state
			// If this is a create, add to the queue
			const _expense = st.get(expense.id);

			if (!_expense) st.set(expense);
			else {
				if (expense.addedOn >= _expense.addedOn) st.set(expense);
			}

			return updateStorage(st, [expense]);
		}

		case Actions.EXPENSE_SAVE_MANY: {
		    if (logging) console.debug('⮂ EXPENSE_SAVE_MANY reducer', action.expenses.map(expense => expense.id).join(','));
		    
			// clone the expenses
			let toProcess = action.expenses.map(expense => new Expense(expense));

			st = state.clone();
			// state.clear();

			const updates: Expense[] = [];

			toProcess.forEach(expense => {
				// Add or update the new expense in the state
				// If this is a create, add to the queue
				const existing = st.get(expense.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (expense.status !== 'D') {
						st.push(expense);
						updates.push(expense);
					}
					else if (logging) console.debug('⮂ EXPENSE_SAVE_MANY: skipping deleted expense not in state');
				}
				else {
					if (expense.addedOn >= existing.addedOn) {
						st.set(expense);
						updates.push(expense);
					}
					else if (logging) console.debug('⮂ Skipping, existing expense had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.EXPENSE_REMOVE: {
			// clone the expense
			const expense = new Expense({...action.expense});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(expense);

			return updateStorage(st, [expense]);
		}

		case Actions.EXPENSE_RESTORE_FAILURE:
		case Actions.EXPENSE_SAVE_FAILURE: {
		    console.warn('EXPENSE_SAVE_FAILURE reducer', action.expense?.id);
		    if (!action.expense?.id) {
				console.warn('EXPENSE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const expense = ensure(Expense, action.expense);
			if (!expense) {
				console.error('Could not find expense after call to save');
				return state;
			}

			st = state.clone();

			const _expenseRef = state.get(expense.id);

			if (_expenseRef && (_expenseRef.addedOn === expense.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _expense = new Expense({..._expenseRef});
				_expense.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_expense.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('expense', _expense).then(r => {
						if (r.isErr()) console.error(`⮂ expenseReducer: failed to save expense with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ expenseReducer: saved expense with fatalError info');
					}).catch(err => console.error(`⮂ expenseReducer: failed to save expense with fatalError info: ${err.message}`));

				st.set(_expense);
			}

			return updateStorage(st);
		}

		case Actions.EXPENSE_SAVE_MANY_FAILURE: {
		    console.warn('EXPENSE_SAVE_MANY_FAILURE reducer', action.expenses.map(expense => expense.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.EXPENSE_RESTORE_SUCCESS:
		case Actions.EXPENSE_SAVE_SUCCESS: {
			if (!action.expense?.id) {
				console.warn('EXPENSE_SAVE_SUCCESS reducer');
                return state;
			}

			const expenseRef = state.get(action.expense?.id);
			if (!expenseRef) {
				console.warn('Could not find expense after call to save');
                return state;
			}

			st = state.clone();

			if (action.expense.addedOn >= expenseRef.addedOn) {
				st.set(action.expense);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const expense = new Expense({...expenseRef, persisted: true});
				st.set(expense);
			}

			return updateStorage(st, [action.expense]);
		}

		case Actions.EXPENSE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ EXPENSE_SAVE_MANY_SUCCESS reducer', action.expenses.map(expense => expense.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const expenses: Expense[] = [];

			action.expenses.forEach(e => {
				const expenseRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!expenseRef) console.warn('Could not find expense after call to save');
				else if (e.addedOn >= expenseRef.addedOn) {
				    st.set(e);
				    expenses.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const expense = new Expense({...expenseRef, persisted: true});
					st.set(expense);
					expenses.push(expense);
				}
			});

			return updateStorage(st, expenses);
		}

		case Actions.EXPENSE_REMOVE_FAILURE: {
		    console.warn('EXPENSE_REMOVE_FAILURE action', action.expense?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.EXPENSE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.EXPENSE_RESTORE_MANY: {
			if (logging) console.debug('⮂ EXPENSE_RESTORE_MANY reducer', action.expenses.map(expense => expense.id).join(','));

			const toProcess = action.expenses.map(expense => new Expense({...expense}));
			st = state.clone();

			const updates: Expense[] = [];

			toProcess.forEach(expense => {
				const existing = st.get(expense.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (expense.addedOn >= existing.addedOn) {
						st.set(expense);
						updates.push(expense);
					}
					else if (logging) console.debug('⮂ EXPENSE_RESTORE_MANY: skipping older version of expense');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (expense.status !== 'D') {
						st.set(expense);
						updates.push(expense);
					}
					else if (logging) console.debug('⮂ EXPENSE_RESTORE_MANY: skipping deleted expense not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.EXPENSE_REMOVE_MANY: {
			if (logging) console.debug('⮂ EXPENSE_REMOVE_MANY reducer', action.expenses.map(expense => expense.id).join(','));

			const toProcess = action.expenses.map(expense => new Expense({...expense}));
			st = state.clone();

			toProcess.forEach(expense => {
				st.delete(expense);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default expenses;